/**
 * useStudentSummary
 *
 * Builds a structured view of a student used by:
 *  - AI companion (system prompt context injection)
 *  - Analytics / inference page (student self-view, parent view)
 *
 * Call buildStudentContextPrompt(summary) to get a compact paragraph
 * suitable for injecting as an AI system-prompt prefix.
 */

import { StorageService } from '@/hooks/services/storageService';
import { AuthStore } from '@/hooks/stores/AuthStore';
import { StudentStore } from '@/hooks/stores/StudentStore';

export interface StudentSummary {
  name: string;
  classLevel: string;
  examBody: string;
  country: string;
  activeCareerPaths: string[];
  topTraits: Array<{ name: string; score: number }>;
  weakTraits: Array<{ name: string; score: number }>;
  recentActivity: string[];           // last 5 activity log entries as readable strings
  completedTopicsCount: number;
  goalsProgress: { active: number; completed: number };
  careerInterests: string[];
  strengths: string[];
  areasForImprovement: string[];
}

// Short display names for trait IDs
const TRAIT_DISPLAY: Record<string, string> = {
  T01: 'Critical Thinking', T02: 'Analytical Reasoning', T03: 'Creativity',
  T04: 'Numerical Ability', T05: 'Problem Solving', T06: 'Technical Aptitude',
  T07: 'Entrepreneurship', T09: 'Strategic Planning', T11: 'Attention to Detail',
  T12: 'Research Skills', T14: 'Empathy', T16: 'Project Management',
  T17: 'Communication', T18: 'Public Speaking', T19: 'Teamwork',
  T22: 'Writing', T23: 'Emotional Intelligence', T25: 'Physical Stamina',
  T28: 'Competitiveness', T30: 'Social Responsibility', T31: 'Self-Discipline',
  T33: 'Leadership', T38: 'Resourcefulness', T40: 'Adaptability',
  T41: 'Resilience', T42: 'Community Service', T44: 'Goal Orientation',
  T46: 'Initiative', T47: 'Time Management', T48: 'Academic Drive',
};

function traitName(id: string): string {
  return TRAIT_DISPLAY[id] ?? id;
}

function describeActivity(entry: any): string {
  const when = entry.timestamp ? new Date(entry.timestamp).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''
  switch (entry.eventType) {
    case 'lesson_completed':
      return `Completed a lesson in ${entry.subjectArea ?? 'a subject'} (score: ${entry.outcomeScore ?? '?'}%) — ${when}`
    case 'quiz_attempted':
      return `Took a quiz in ${entry.subjectArea ?? 'a subject'} — scored ${entry.outcomeScore ?? '?'}% — ${when}`
    case 'game_played':
      return `Played ${(entry.meta as any)?.gameName ?? 'a game'} — score ${(entry.meta as any)?.score ?? '?'} — ${when}`
    case 'goal_completed':
      return `Completed a goal — ${when}`
    case 'extracurricular':
      return `Did ${(entry.meta as any)?.activityName ?? 'an extracurricular activity'} — ${when}`
    case 'competition':
      return `Competed in ${(entry.meta as any)?.name ?? 'a competition'} — ${when}`
    case 'exam_result_uploaded':
      return `Uploaded ${(entry.meta as any)?.examType ?? 'exam'} result: ${(entry.subjectArea ?? '')} ${(entry.meta as any)?.grade ?? ''} — ${when}`
    case 'ai_chat_session':
      return `Had an AI study session — ${when}`
    default:
      return `${entry.eventType?.replace(/_/g, ' ') ?? 'Activity'} — ${when}`
  }
}

export function buildStudentSummary(student: any): StudentSummary {
  const prefs = student.preferences ?? {}
  const name = `${student.firstName ?? ''} ${student.lastName ?? ''}`.trim() || 'Student'
  const classLevel = prefs.classLevel ?? student.classLevel ?? 'Unknown'
  const examBody = prefs.examBody ?? 'Unknown'
  const country = prefs.country ?? 'Unknown'

  // Active career paths — from journey records
  const journeyRecords: any[] = StorageService.read('journeyRecords')
  const myJourneys = journeyRecords.filter((j: any) => j.userId === student.userId)
  const activeCareerPaths = myJourneys.map((j: any) => j.pathName ?? j.pathId ?? 'A career path')

  // Traits from traitProfile
  const profile: Record<string, number> = student.traitProfile ?? {}
  const traitEntries = Object.entries(profile)
    .map(([id, score]) => ({ name: traitName(id), score: Math.round(score as number) }))
    .sort((a, b) => b.score - a.score)
  const topTraits = traitEntries.slice(0, 5)
  const weakTraits = traitEntries.slice(-3).reverse()

  // Recent activity (last 5)
  const activityLog: any[] = student.activityLog ?? []
  const recentActivity = activityLog
    .slice(-5)
    .reverse()
    .map(describeActivity)

  // Completed topics across all journeys
  const completedTopicsCount = myJourneys.reduce(
    (sum: number, j: any) => sum + (j.completedModuleIds?.length ?? 0),
    0,
  )

  // Goals
  const allGoals: any[] = StorageService.read('goals')
  const myGoals = allGoals.filter((g: any) => g.userId === student.userId || g.studentId === student._id)
  const goalsProgress = {
    active: myGoals.filter((g: any) => g.status !== 'completed').length,
    completed: myGoals.filter((g: any) => g.status === 'completed').length,
  }

  // Career interests
  const careerInterests: string[] = student.careerInterests ?? []

  // Derive strengths from top traits + high-score activities
  const strengths = [
    ...topTraits.slice(0, 3).map((t) => t.name),
    ...careerInterests.slice(0, 2),
  ].filter(Boolean)

  // Areas for improvement from weak traits
  const areasForImprovement = weakTraits.map((t) => t.name)

  return {
    name, classLevel, examBody, country,
    activeCareerPaths, topTraits, weakTraits,
    recentActivity, completedTopicsCount,
    goalsProgress, careerInterests,
    strengths, areasForImprovement,
  }
}

export function buildStudentContextPrompt(summary: StudentSummary): string {
  const paths = summary.activeCareerPaths.length
    ? summary.activeCareerPaths.join(', ')
    : 'no career paths selected yet'
  const topTraitStr = summary.topTraits.slice(0, 3).map((t) => t.name).join(', ') || 'not yet assessed'
  const recent = summary.recentActivity[0] ?? 'no recent activity'

  return (
    `You are an encouraging AI companion for ${summary.name}, a ${summary.classLevel} student ` +
    `(${summary.examBody}, ${summary.country}). ` +
    `They are exploring: ${paths}. ` +
    `Their top strengths are: ${topTraitStr}. ` +
    `Most recent activity: ${recent}. ` +
    `Goals: ${summary.goalsProgress.active} active, ${summary.goalsProgress.completed} completed. ` +
    `Career interests: ${summary.careerInterests.slice(0, 3).join(', ') || 'not specified'}. ` +
    `Be warm, specific, age-appropriate, and always relate advice back to their actual profile. ` +
    `Never lecture — guide and encourage.`
  )
}

/** Hook version — resolves the current logged-in student automatically */
export function useStudentSummary(): StudentSummary | null {
  // Primary: read from StudentStore (encrypted, always up-to-date after login/sync)
  const storeStudent = StudentStore((s) => s.student)
  if (storeStudent) return buildStudentSummary(storeStudent)

  // Fallback: prototype/legacy mode reads from StorageService array
  const user = AuthStore.getState().currentUser
  if (!user) return null
  const students: any[] = StorageService.read('students')
  const student = students.find((s: any) => s.userId === user._id || s.email === user.email)
  if (!student) return null
  return buildStudentSummary(student)
}
